import { Engine } from "../Engine";
import { GameConstants } from "../GameConstants";
import { MathUtils } from "../utils/MathUtils";
import { Arrow } from "../turrets/shots/Arrow";
import { Mortar } from "../turrets/shots/Mortar";
import { Mine } from "../turrets/shots/Mine";
import { Event } from "../events/Event";
import { FireTurret, BzztTurret } from "../turrets";

export class Enemy {

    public type: string;
    public subtype: string;
    public id: number;
    public imageId: number;
    public editorId: string;
    public split: string;
    public splitted: boolean;
    public path: {r: number, c: number}[];
    public life: number;
    public maxLife: number;
    public speed: number;
    public x: number;
    public y: number;
    public prevX: number;
    public prevY: number;
    public creationTick: number;
    public value: number;
    public boundingRadius: number;
    public deltaYBoundingRadius: number;
    public l: number;
    public affectedByGlue: boolean;
    public glueIntensity: number;
    public affectedByBzztBullet: boolean;
    public bzztBulletIntensity: number;
    public bzztBulletDurationTicks: number;
    public bzztTicksCounter: number;
    public affectedBySpeed: boolean;
    public speedDurationTicks: number;
    public speedBoostMult: number;
    public speedTicksCounter: number;
    public hasBeenTeleported: boolean;
    public teleporting: boolean;
    public modifiers: {[key: string]: string};
    
    protected enemyData: any;
    protected t: number;
    protected engine: Engine;
    

    constructor (type: string, subtype: string, creationTick: number, engine: Engine, path: {r: number, c: number}[], l?: number, splitted?: boolean) {
        
        this.id = engine.enemyId;
        engine.enemyId ++;

        this.path = path;

        this.modifiers = {};

        this.creationTick = creationTick;
        this.engine = engine;

        this.type = type;
        this.subtype = subtype;
        this.enemyData = this.engine.enemyData[this.type][this.subtype];

        this.life =  this.enemyData.life;
        this.maxLife = this.enemyData.life;
        this.value = this.enemyData.value;
        this.speed = this.enemyData.speed / 100;
        this.imageId = this.enemyData.id;
        this.editorId = this.enemyData.editorId;
        this.split = this.enemyData.split;
        this.splitted = splitted || false;

        this.affectedByGlue = false;
        this.glueIntensity = 0;
        this.affectedByBzztBullet = false;
        this.bzztBulletIntensity = 0;
        this.bzztBulletDurationTicks = 0;
        this.bzztTicksCounter = 0;
        this.hasBeenTeleported = false;
        this.teleporting = false;
        this.speedBoostMult = 1;

        this.l = l || 0;
        this.t = 0;

        let p = MathUtils.getPathPosition(this.l, this.path);

        if (!p) {
            p = {x: 0, y: 0};
        }

        this.x = p.x;
        this.y = p.y; 
        this.prevX = this.x;
        this.prevY = this.y;

        this.boundingRadius = .3;
        this.deltaYBoundingRadius = -.35;

        switch (this.type) {
            case GameConstants.ENEMY_WOOD:
                this.modifiers[GameConstants.TURRET_FIRE] = "weak";
                break;
            case GameConstants.ENEMY_WATER:
                this.modifiers[GameConstants.TURRET_FIRE] = "weak";
                break;
            case GameConstants.ENEMY_ICE:
                this.modifiers[GameConstants.TURRET_FIRE] = "weak";
                break;
            case GameConstants.ENEMY_FIRE:
                this.modifiers[GameConstants.TURRET_FIRE] = "strong";
                break;
            case GameConstants.ENEMY_ELECTRIC:
                this.modifiers[GameConstants.TURRET_BZZT] = "strong";
                break;
            default:
                break;
        }
    }

    public destroy(): void {
        // de momento nada
    }

    public update(): void {

        if (this.teleporting) {

            this.t ++;

            if (this.t === 8) {
                this.teleporting = false;
            }
            return;
        }

        let speed = this.speed;

        // si esta encima de pegamento hacer que vaya mas lento
        if (this.affectedByGlue) {

            speed = MathUtils.fixNumber(this.speed / this.glueIntensity);   
        }

        if (this.splitted && this.creationTick + 5 > this.engine.ticksCounter) {
            speed *= .5;
        }

        if (this.affectedByBzztBullet) {

            speed = MathUtils.fixNumber(this.speed / this.bzztBulletIntensity);

            this.bzztTicksCounter++;

            if (this.bzztTicksCounter === this.bzztBulletDurationTicks) {
                this.affectedByBzztBullet = false;
            } 
        }

        if (this.affectedBySpeed) {

            speed *= this.speedBoostMult;

            this.speedTicksCounter++;

            if (this.speedTicksCounter === this.speedDurationTicks) {
                this.affectedBySpeed = false;
            } 
        }

        if (this.engine.freezeAttackActive && this.type !== GameConstants.ENEMY_WIND) {
            speed *= .2;
        }
       
        this.l = MathUtils.fixNumber(this.l + speed);

        this.prevX = this.x;
        this.prevY = this.y;

        if (this.l >= this.path.length - 1) {

            this.x = this.path[this.path.length - 1].c;
            this.y = this.path[this.path.length - 1].r;

            this.engine.onEnemyReachedExit(this);

        } else {

            const p = MathUtils.getPathPosition(this.l, this.path);

            this.x = p.x;
            this.y = p.y;
        }
    }

    public teleport(teleportDistance: number): void {

        this.hasBeenTeleported = true;
        this.teleporting = true;
        this.t = 0;

        this.l -= teleportDistance;

        if (this.l < 0) {
            this.l = 0;
        }

        const p = MathUtils.getPathPosition(this.l, this.path);

        this.x = p.x;
        this.y = p.y;
    }

    public hitByBzztBullet(glueBulletIntensity: number, glueBulletsDurationTicks: number): void {
        
        this.bzztTicksCounter = 0;
        this.affectedByBzztBullet = true;
        this.bzztBulletIntensity = glueBulletIntensity;
        this.bzztBulletDurationTicks = glueBulletsDurationTicks;
    }

    public glue(glueIntensity: number): void {

        this.affectedByGlue = true;
        this.glueIntensity = glueIntensity;
    }

    public killInstantly(): void {

        this.life = 0;
        this.engine.onEnemyKilled(this);
    }

    public hit(damage: number, bullet?: Arrow, mortar?: Mortar, mine?: Mine, fireTurret?: FireTurret, bzztTurret?: BzztTurret): void {
        
        if (this.life <= 0) {
            return;
        }

        let modifier = 1;

        if (fireTurret) {
            if (this.modifiers[GameConstants.TURRET_FIRE] === "weak") {
                modifier = this.engine.modifiers.weak;
            } else if (this.modifiers[GameConstants.TURRET_FIRE] === "strong") {
                modifier = this.engine.modifiers.strong;
            }
        } else if (bzztTurret) {
            if (this.modifiers[GameConstants.TURRET_BZZT] === "strong") {
                modifier = this.engine.modifiers.strong;
            }

            // this.engine.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_BZZT_HIT, [[this] ]));
            this.hitByBzztBullet(bzztTurret.intensity[bzztTurret.level - 1], bzztTurret.duration[bzztTurret.level - 1]);
        }
        
        this.life -= MathUtils.fixNumber(damage * modifier);

        if (bullet && bullet.turret) {
            bullet.turret.inflicted += Math.round(damage);
        } else if (mortar && mortar.turret) {
            mortar.turret.inflicted += Math.round(damage);
        } else if (mine && mine.turret) {
            mine.turret.inflicted += Math.round(damage);
        } else if (fireTurret) {
            fireTurret.inflicted += Math.round(damage);
        } else if (bzztTurret) {
            bzztTurret.inflicted += Math.round(damage);
        }

        if (this.life <= 0) {
            this.life = 0;
            this.engine.onEnemyKilled(this);
        }
    }

    public restoreHealth(value: number): void {

        this.life += MathUtils.fixNumber(this.maxLife * value);

        if (this.life > this.maxLife) {
            this.life = this.maxLife;
        }

        this.engine.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_HEAL, [this]));
    }
    
    public improveSpeed(speed: number): void {

        this.speedDurationTicks = 10; 
        this.speedTicksCounter = 0;
        this.affectedBySpeed = true;
        this.speedBoostMult = speed;

        this.engine.eventDispatcher.dispatchEvent(new Event(Event.ENEMY_SPEED, [this]));
    }

    public getNextPosition(deltaTicks: number): {x: number, y: number} {

        let speed = this.speed;

        if (this.affectedByGlue) {
            speed = MathUtils.fixNumber(this.speed / this.glueIntensity);
        }

        let l = MathUtils.fixNumber(this.l + speed * deltaTicks);

        const p = MathUtils.getPathPosition(l, this.path);

        if (!p) {
            return null;
        }

        return {x: p.x, y: p.y};
    }
}
